import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { api, ApiError, setUnauthorizedHandler } from "@/api/client";
import type { BrandRole, Me } from "@/api/types";

interface AuthValue {
  me: Me | null;
  isLoading: boolean;
  /** Server unreachable (we still render the shell so pages can show their offline states). */
  offline: boolean;
  error: unknown;
  refetch: () => void;
  logout: () => Promise<void>;
  isAdmin: boolean;
  brandRole: (brandId: string | null | undefined) => BrandRole | "admin" | null;
  /** Permission check from /api/me — default deny (server stays authoritative). Check `isLoading` first. */
  can: (perm: string, brandId?: string | null) => boolean;
  /** Agent persona name (settings agent.name), default "Mimic". */
  agentName: string;
}

const AuthContext = createContext<AuthValue | null>(null);

function normalizeMe(res: unknown): Me | null {
  if (!res || typeof res !== "object") return null;
  const o = res as Record<string, unknown>;
  const u = (o.user && typeof o.user === "object" ? o.user : o) as Record<string, unknown>;
  if (!u.id && !u.email) return null;
  const brandRows = Array.isArray(o.brands) ? (o.brands as { id?: string; brandId?: string; role?: string | null }[]) : [];
  const fromBrands = brandRows.filter((b) => b.role).map((b) => ({ brandId: String(b.id ?? b.brandId), role: b.role as BrandRole }));
  const perms = o.permissions && typeof o.permissions === "object" ? (o.permissions as Me["permissions"]) : undefined;
  return {
    id: String(u.id ?? ""),
    email: String(u.email ?? ""),
    name: (u.name as string) || undefined,
    orgRole: (o.orgRole ?? u.orgRole ?? u.org_role ?? "member") as Me["orgRole"],
    brandRoles: (u.brandRoles ?? o.brandRoles ?? o.brandAccess ?? (fromBrands.length ? fromBrands : undefined)) as Me["brandRoles"],
    permissions: perms,
    agentName: typeof o.agentName === "string" ? o.agentName : undefined,
    via: typeof o.via === "string" ? o.via : undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const q = useQuery({
    queryKey: ["me"],
    queryFn: async () => normalizeMe(await api("/api/me", { noAuthRedirect: true })),
    retry: (count, err) => err instanceof ApiError && err.isNetwork && count < 1,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    setUnauthorizedHandler(() => {
      const here = window.location.pathname + window.location.search;
      if (here.startsWith("/login") || here.startsWith("/invite")) return;
      qc.clear();
      navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, qc]);

  const unauth = q.error instanceof ApiError && q.error.status === 401;
  useEffect(() => {
    if (unauth && !location.pathname.startsWith("/login") && !location.pathname.startsWith("/invite")) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
    }
  }, [unauth, location.pathname, location.search, navigate]);

  const logout = useCallback(async () => {
    try {
      await api("/api/auth/logout", { method: "POST", body: {}, noAuthRedirect: true });
    } catch {
      /* ignore */
    }
    qc.clear();
    navigate("/login", { replace: true });
  }, [navigate, qc]);

  const value = useMemo<AuthValue>(() => {
    const me = q.data ?? null;
    const isAdmin = me?.orgRole === "owner" || me?.orgRole === "admin";
    const roles = me?.brandRoles;
    return {
      me,
      isLoading: q.isLoading,
      offline: q.error instanceof ApiError && q.error.isNetwork,
      error: q.error,
      refetch: () => void q.refetch(),
      logout,
      isAdmin,
      agentName: me?.agentName || "Mimic",
      // Default deny: unknown user / no permission list / brand not in the list → false. While /api/me is
      // loading, callers check `isLoading` and render skeletons. (The server enforces everything regardless —
      // this only decides which privileged affordances are shown.)
      can: (perm, brandId) => {
        if (!me) return false;
        const p = me.permissions;
        if (!p) return false;
        if (brandId) return p.brands?.[brandId]?.includes(perm) ?? false;
        return p.org?.includes(perm) ?? false;
      },
      brandRole: (brandId) => {
        if (!me) return null;
        if (isAdmin) return "admin";
        if (!brandId || !roles) return null;
        if (Array.isArray(roles)) return roles.find((r) => r.brandId === brandId)?.role ?? null;
        return roles[brandId] ?? null;
      },
    };
  }, [q.data, q.isLoading, q.error, q.refetch, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const OUTSIDE: AuthValue = {
  me: null,
  isLoading: false,
  offline: false,
  error: null,
  refetch: () => {},
  logout: async () => {},
  isAdmin: false,
  brandRole: () => null,
  can: () => false,
  agentName: "Mimic",
};

/** Auth state. Outside the provider (isolated component tests) it returns an anonymous, deny-all value. */
export function useAuth(): AuthValue {
  return useContext(AuthContext) ?? OUTSIDE;
}

/** Display name for the greeting. */
export function displayName(me: Me | null): string {
  if (!me) return "there";
  return me.name?.split(" ")[0] || me.email.split("@")[0] || "there";
}
